package co.maytes.standupbot.dashboard.features.standupruns

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.features.settings.RetentionCopy
import co.maytes.standupbot.shared.dto.promptarchive.PromptArchiveDto
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px

@Composable
fun PromptArchivePanel(workspaceId: String, runId: String) {
    val api = rememberApiClient()
    val auth = rememberAuthStore()
    val effectiveWorkspaceId = workspaceId.ifBlank { auth.currentUser?.workspace?.id.orEmpty() }
    var entries by remember { mutableStateOf<List<PromptArchiveDto>>(emptyList()) }
    var loading by remember { mutableStateOf(false) }
    var loadError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(effectiveWorkspaceId, runId) {
        if (effectiveWorkspaceId.isBlank() || runId.isBlank()) return@LaunchedEffect
        loading = true
        when (
            val res =
                api.get<List<PromptArchiveDto>>(
                    "/api/v1/workspaces/$effectiveWorkspaceId/runs/$runId/prompt-archive",
                )
        ) {
            is Result.Ok -> {
                entries = res.value
                loadError = null
            }
            is Result.Err -> loadError = res.error.displayMessage
        }
        loading = false
    }

    Column(modifier = Modifier.fillMaxWidth().margin(top = Spacing.lg)) {
        SpanText(
            text = "Prompt archive",
            modifier = Modifier
                .margin(bottom = Spacing.xs)
                .fontSize(Typography.bodySmall.size)
                .fontWeight(600)
                .color(Palette.textSecondary),
        )
        when {
            loading && entries.isEmpty() -> SpanText(
                text = "Loading prompts…",
                modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textTertiary),
            )
            loadError != null -> SpanText(
                text = loadError!!,
                modifier = Modifier.fontSize(Typography.caption.size).color(Palette.error),
            )
            entries.isEmpty() -> SpanText(
                text = "No archived prompts for this run.",
                modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textTertiary),
            )
            else -> entries.forEach { ArchiveEntryCard(it) }
        }
    }
}

@Composable
private fun ArchiveEntryCard(entry: PromptArchiveDto) {
    var systemOpen by remember(entry.id) { mutableStateOf(false) }
    var userOpen by remember(entry.id) { mutableStateOf(false) }
    var responseOpen by remember(entry.id) { mutableStateOf(false) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = Spacing.sm)
            .padding(Spacing.sm)
            .borderRadius(Radius.md)
            .styleModifier {
                property("border", "1px solid ${surfaceSubtleRgb()}")
            },
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .fillMaxWidth()
                .gap(Spacing.xs)
                .styleModifier { property("flex-wrap", "wrap") },
        ) {
            Pill(text = entry.purpose, tone = PillTone.Accent)
            Pill(text = "v${entry.promptVersion}", tone = PillTone.Subtle)
            entry.model?.let { Pill(text = it, tone = PillTone.Subtle) }
            val tokens =
                buildString {
                    if (entry.inputTokens != null || entry.outputTokens != null) {
                        append(entry.inputTokens ?: 0)
                        append(" in / ")
                        append(entry.outputTokens ?: 0)
                        append(" out")
                    }
                }
            if (tokens.isNotEmpty()) {
                SpanText(
                    text = tokens,
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textTertiary)
                        .styleModifier { property("font-family", Typography.FONT_MONO) },
                )
            }
            entry.costCents?.let { cents ->
                SpanText(
                    text = formatCostCents(cents),
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textTertiary)
                        .styleModifier { property("font-family", Typography.FONT_MONO) },
                )
            }
        }

        val textPurgedAt = entry.textPurgedAt
        if (textPurgedAt != null) {
            SpanText(
                text = RetentionCopy.promptTextRemoved(textPurgedAt),
                modifier = Modifier
                    .margin(top = Spacing.xs)
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary),
            )
            return@Column
        }

        CollapsibleBlock(
            label = "System prompt",
            open = systemOpen,
            onToggle = { systemOpen = !systemOpen },
            body = entry.systemPrompt,
        )
        CollapsibleBlock(
            label = "User prompt",
            open = userOpen,
            onToggle = { userOpen = !userOpen },
            body = entry.userPrompt,
        )
        CollapsibleBlock(
            label = "Response",
            open = responseOpen,
            onToggle = { responseOpen = !responseOpen },
            body = entry.responseText ?: "(no response — call failed or returned empty)",
        )
    }
}

@Composable
private fun CollapsibleBlock(
    label: String,
    open: Boolean,
    onToggle: () -> Unit,
    body: String,
) {
    Column(modifier = Modifier.fillMaxWidth().margin(top = Spacing.xs)) {
        Box(
            modifier = Modifier
                .padding(topBottom = 4.px, leftRight = Spacing.sm)
                .borderRadius(Radius.full)
                .backgroundColor(Palette.surfaceSubtle)
                .color(Palette.accent)
                .cursor(Cursor.Pointer)
                .onClick { onToggle() },
        ) {
            SpanText(
                text = if (open) "Hide $label" else "Show $label",
                modifier = Modifier.fontSize(Typography.caption.size),
            )
        }
        if (open) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(top = Spacing.xs)
                    .padding(Spacing.md)
                    .borderRadius(Radius.md)
                    .backgroundColor(Palette.surfaceSubtle)
                    .styleModifier {
                        property("font-family", Typography.FONT_MONO)
                        property("font-size", "12px")
                        property("white-space", "pre-wrap")
                        property("overflow-wrap", "anywhere")
                        property("max-height", "400px")
                        property("overflow-y", "auto")
                        property("color", "rgb(9, 9, 11)")
                    },
            ) {
                SpanText(text = body)
            }
        }
    }
}

private enum class PillTone { Accent, Subtle }

@Composable
private fun Pill(text: String, tone: PillTone) {
    val color = if (tone == PillTone.Accent) Palette.accent else Palette.textSecondary
    Box(
        modifier = Modifier
            .padding(topBottom = 2.px, leftRight = Spacing.sm)
            .borderRadius(Radius.full)
            .backgroundColor(Palette.surfaceSubtle)
            .color(color),
    ) {
        SpanText(
            text = text,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(600),
        )
    }
}

private fun formatCostCents(cents: Int): String {
    val dollars = cents / 100
    val remainder = cents % 100
    val padded = remainder.toString().padStart(2, '0')
    return "$$dollars.$padded"
}
